import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSignupViewModel } from "../../viewmodel/useSignupViewModel";
import { useInlineLoading } from "@/shared/loading";
import { routes } from "@/app/navigation/routes";
import andyHdLogo from "@/assets/andyhdlogo.png";
import farmhouseBg from "@/assets/images/login-farmhouse-bg.jpg";
import styles from "./SignupForm.module.css";
import { PHONE_DIAL_CODE_OPTIONS } from "../../constants/phoneDialCodes";

/**
 * SignupForm Component
 * Matches exact UI mockup with centered modern card, farmhouse background,
 * pill inputs, and bottom-left copyright footer.
 * Preserves all authentication logic and bindings intact.
 */
export const SignupForm = () => {
  const location = useLocation();
  const {
    credentials,
    isLoading,
    firstNameError,
    lastNameError,
    emailError,
    passwordError,
    confirmPasswordError,
    updateFirstName,
    updateLastName,
    updateEmail,
    updatePassword,
    updateConfirmPassword,
    handleSubmit,
    mobileError,
    dialCode,
    setDialCode,
    nationalNumber,
    updateNationalNumber,
  } = useSignupViewModel();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const BUTTON_ID = "signup-button";
  const { isLoading: isInlineLoading } = useInlineLoading(BUTTON_ID);
  const buttonLoading = isLoading || isInlineLoading;

  return (
    <div className={styles.pageContainer} style={{ backgroundImage: `url(${farmhouseBg})` }}>
      <div className={styles.pageOverlay} />

      {/* Top Left Header / Logo */}
      <header className={styles.header}>
        <Link to={routes.landing} className={styles.logoWrap}>
          <img src={andyHdLogo} alt="Andy Mascot" className={styles.logoImg} />
          <span className={styles.logoText}>ANDY</span>
        </Link>
      </header>

      {/* Center Main Signup Card */}
      <main className={styles.mainContent}>
        <div className={styles.cardWrapper}>
          <div className={styles.signupCard}>
            <h1 className={styles.title}>
              Let&apos;s <span className={styles.titleGreen}>Get Started</span>
            </h1>
            <p className={styles.subtitle}>Sign up your account</p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              {/* First Name & Last Name */}
              <div className={styles.nameRow}>
                <div className={styles.inputGroup}>
                  <label className={styles.label} htmlFor="firstName">
                    First Name
                  </label>
                  <div
                    className={`${styles.inputBox} ${firstNameError ? styles.inputBoxError : ""}`}
                  >
                    <input
                      id="firstName"
                      type="text"
                      className={styles.input}
                      value={credentials.firstName}
                      onChange={(e) => updateFirstName(e.target.value)}
                      placeholder="First name"
                    />
                  </div>
                  {firstNameError && <div className={styles.errorMessage}>{firstNameError}</div>}
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label} htmlFor="lastName">
                    Last Name
                  </label>
                  <div
                    className={`${styles.inputBox} ${lastNameError ? styles.inputBoxError : ""}`}
                  >
                    <input
                      id="lastName"
                      type="text"
                      className={styles.input}
                      value={credentials.lastName}
                      onChange={(e) => updateLastName(e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                  {lastNameError && <div className={styles.errorMessage}>{lastNameError}</div>}
                </div>
              </div>

              {/* Email */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">
                  Email
                </label>
                <div
                  className={`${styles.inputBox} ${styles.inputBoxTinted} ${
                    emailError ? styles.inputBoxError : ""
                  }`}
                >
                  <span className={styles.inputIcon}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    value={credentials.email}
                    onChange={(e) => updateEmail(e.target.value)}
                    placeholder="rsai02299@gmail.com"
                  />
                </div>
                {emailError && <div className={styles.errorMessage}>{emailError}</div>}
              </div>

              {/* Mobile Number */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="phone">
                  Mobile Number
                </label>
                <div className={styles.phoneFieldGroup}>
                  <div className={styles.dialSelectWrapper}>
                    <select
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      className={styles.dialSelect}
                      aria-label="Country Dial Code"
                    >
                      {PHONE_DIAL_CODE_OPTIONS.map((item) => (
                        <option key={item.dialCode} value={item.dialCode}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <span className={styles.selectChevron} aria-hidden="true">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </div>

                  <div
                    className={`${styles.inputBox} ${styles.phoneInputBox} ${
                      mobileError ? styles.inputBoxError : ""
                    }`}
                  >
                    <input
                      id="phone"
                      type="tel"
                      className={styles.input}
                      value={nationalNumber}
                      maxLength={10}
                      minLength={10}
                      onChange={(e) => updateNationalNumber(e.target.value)}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>
                {mobileError && <div className={styles.errorMessage}>{mobileError}</div>}
              </div>

              {/* Password */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="password">
                  Password
                </label>
                <div
                  className={`${styles.inputBox} ${styles.inputBoxTinted} ${
                    passwordError ? styles.inputBoxError : ""
                  }`}
                >
                  <span className={styles.inputIcon}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className={styles.input}
                    value={credentials.password}
                    onChange={(e) => updatePassword(e.target.value)}
                    placeholder="•••••••••"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
              </div>

              {/* Confirm Password */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <div
                  className={`${styles.inputBox} ${
                    confirmPasswordError ? styles.inputBoxError : ""
                  }`}
                >
                  <span className={styles.inputIcon}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className={styles.input}
                    value={credentials.confirmPassword}
                    onChange={(e) => updateConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={
                      showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                    }
                    aria-pressed={showConfirmPassword}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {confirmPasswordError && (
                  <div className={styles.errorMessage}>{confirmPasswordError}</div>
                )}
              </div>

              {/* Continue Button */}
              <button
                id={BUTTON_ID}
                type="submit"
                className={styles.continueButton}
                disabled={buttonLoading}
              >
                <span>{buttonLoading ? "Loading..." : "Continue"}</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </form>

            {/* Login Link */}
            <div className={styles.loginLink}>
              Already have an account?{" "}
              <Link to={routes.login} state={location.state} className={styles.loginLinkText}>
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Footer */}
      <footer className={styles.footer}>
        <span>© 2026 ANDY. All rights reserved.</span>
        <span className={styles.footerDivider}>|</span>
        <Link to="#" className={styles.footerLink}>
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
};
